import { isBoolean, isEmpty } from "lodash";

import store from "../../../../store";
import Config from "../../../config";
import PageConfig from "../config";
import GenericDataAPI from "../../../services/api/generic";
import PaymentServiceAPI from "../../../services/api/payments";
import AppLogger from "../../../utils/logger";
import { setAppSnackNotification } from "../../../store/actions/application";

const { PAYMENT_METHOD_ERROR } = PageConfig;

// Method: load Paypal as script tags
const loadPaypalScript = () => {
  const paypalScript = document.createElement("script");
  paypalScript.src = "https://www.paypalobjects.com/api/checkout.js";
  paypalScript.async = true;

  document.body.appendChild(paypalScript);
};

// Method: load Stripe as script tags
const loadStripeScript = () => {
  const stripeScript = document.createElement("script");
  stripeScript.src = "https://js.stripe.com/v3/";
  stripeScript.async = true;
  document.body.appendChild(stripeScript);
};

// Method: format pricing <-- move to utility function
const formatNumberToHumanReadablePrice = ({ amount, currency }) => {
  const label = currency === "LKR" ? "Rs." : currency;
  const output = `${label} ${amount.toFixed(2)}`;
  return output;
};

const generateMessagePromoCodeApiError = ({ t, apiResponse }) => {
  const errorCode = apiResponse.body.code;
  switch (errorCode) {
    case 1400:
      return t("payment.invalidPromoCode");
    case 1401:
      return t("payment.promoCodeExpired");
    case 1311:
      return t("payment.promoCodeValidOnlyForNewAccounts");
    case 1312:
      return t("payment.promoCodeInvalidForPackage");
    default:
      return t("errors.unknownErrorOccurred");
  }
};

const initGenericPaymentMethods = async ({ setGenericPaymentMethods }) => {
  try {
    const response = await GenericDataAPI.getPaymentMethods();
    if (!response.success) {
      store.dispatch(
        setAppSnackNotification({
          severity: "error",
          message: response.body?.message ?? "Can get payment methods",
        })
      );
      return;
    }
    setGenericPaymentMethods(response.body);
  } catch (error) {
    AppLogger.exception(error);
    store.dispatch(
      setAppSnackNotification({
        severity: "error",
        message: "Can get payment methods",
      })
    );
  }
};

const initPaymentLinkData = async ({ id, setPaymentLinkData }) => {
  try {
    const response = await PaymentServiceAPI.getPaymentLinkData({ id });
    if (!response.success) {
      store.dispatch(
        setAppSnackNotification({
          severity: "error",
          message: response.body?.message ?? "Can get initialize payment link",
        })
      );
      return;
    }
    setPaymentLinkData(response.body);
  } catch (error) {
    AppLogger.exception(error);
    store.dispatch(
      setAppSnackNotification({
        severity: "error",
        message: "Can get initialize payment link",
      })
    );
  }
};

const initPaymentCurrencyByPaymentLinkData = ({
  paymentLinkData,
  setCurrency,
}) => {
  try {
    const { BASE_COUNTRY_CODE, BASE_CURRENCY, CURRENCY_META } = Config;
    if (isEmpty(paymentLinkData.post?.personalInfo?.residentCountry)) {
      const currency = CURRENCY_META.find(
        (element) => element.code === BASE_CURRENCY
      );
      setCurrency(currency);
      return;
    }
    const personalInfo = paymentLinkData.post.personalInfo;
    const residentCountryCode = personalInfo.residentCountry.code;
    const currencyCode =
      residentCountryCode === BASE_COUNTRY_CODE ? "LKR" : "USD";
    const currency = CURRENCY_META.find(
      (element) => element.code === currencyCode
    );
    setCurrency(currency);
  } catch (error) {
    AppLogger.exception(error);
    store.dispatch(
      setAppSnackNotification({
        severity: "error",
        message: "Can get initialize payment currency",
      })
    );
  }
};

const simulateCheckoutTier = async ({
  paymentLinkData,
  setSimulatedCheckoutTier,
}) => {
  try {
    const { post } = paymentLinkData;
    const residentCountryCode = post?.personalInfo?.residentCountry?.id ?? "LK";
    const forceLKR = residentCountryCode === "LK";
    const requestBody = {
      paymentLinkId: paymentLinkData.linkId,
      pricingTierId: paymentLinkData.pricingTier.id,
      forceLKR,
    };
    const response = await PaymentServiceAPI.simulatePaymentSession({
      postId: post.id,
      data: requestBody,
    });
    if (!response.success) {
      const message = response.body?.message ?? "Error Occurred, Try Again";
      store.dispatch(
        setAppSnackNotification({
          severity: "error",
          message,
        })
      );
      return;
    }
    setSimulatedCheckoutTier(response.body);
  } catch (error) {
    AppLogger.exception(error);
  }
};

const simulateCheckoutTierWithPromoCode = async ({
  paymentLinkData,
  promoCode,
  setFormErrors,
  setSimulatedCheckoutTier,
  setPromoCodeView,
  setShouldApplyPromoCode,
  t,
}) => {
  try {
    const { post } = paymentLinkData;
    const residentCountryCode = post?.personalInfo?.residentCountry?.id ?? "LK";
    const forceLKR = residentCountryCode === "LK";
    const requestBody = {
      paymentLinkId: paymentLinkData.linkId,
      pricingTierId: paymentLinkData.pricingTier.id,
      forceLKR,
      promoCode,
    };

    const response =
      await PaymentServiceAPI.simulatePaymentSessionWithPromoCode({
        postId: post.id,
        data: requestBody,
      });
    if (!response.success) {
      const message = generateMessagePromoCodeApiError({
        t,
        apiResponse: response,
      });
      store.dispatch(
        setAppSnackNotification({
          severity: "error",
          message,
        })
      );
      setFormErrors({ promoCode: message });
      setShouldApplyPromoCode(false);
      return;
    }
    setSimulatedCheckoutTier(response.body);
    setPromoCodeView("APPLIED_PROMO_CODE");
    setShouldApplyPromoCode(true);
  } catch (error) {
    AppLogger.exception(error);
  }
};

const calculateInvoiceObject = ({ checkoutTier, paymentLinkData }) => {
  const output = { packageFee: "", totalDue: "", currency: "" };
  try {
    if (isEmpty(checkoutTier)) {
      return output;
    }
    if (paymentLinkData.currency && paymentLinkData.amount) {
      output.currency = paymentLinkData.currency;
      output.packageFee = paymentLinkData.amount;
      output.totalDue = paymentLinkData.amount;
    } else {
      output.currency = checkoutTier.currency;
      output.packageFee = checkoutTier.amountBeforeDiscount;
      output.totalDue = checkoutTier.amountBeforeDiscount;
      if (!isEmpty(checkoutTier.promoCode)) {
        output.totalDue = checkoutTier.amount;
        const discountAmount = Math.abs(
          checkoutTier.amountBeforeDiscount - checkoutTier.amount
        );
        const discountPercentage =
          (discountAmount / checkoutTier.amountBeforeDiscount) * 100;
        output.discountAmount = formatNumberToHumanReadablePrice({
          currency: output.currency,
          amount: discountAmount,
        });
        output.discountPercentage = discountPercentage;
      }
    }
    output.packageFee = formatNumberToHumanReadablePrice({
      amount: output.packageFee,
      currency: output.currency,
    });
    output.totalDue = formatNumberToHumanReadablePrice({
      amount: output.totalDue,
      currency: output.currency,
    });
  } catch (error) {
    AppLogger.exception(error);
  }
  return output;
};

const validatePaymentForm = ({
  selectedPaymentMethod,
  secondaryPaymentMethod,
  agreedToTerms,
}) => {
  const errors = {};
  if (isEmpty(selectedPaymentMethod)) {
    errors.selectedPaymentMethod = PAYMENT_METHOD_ERROR.REQUIRED;
  }
  if (isEmpty(secondaryPaymentMethod)) {
    errors.secondaryPaymentMethod = PAYMENT_METHOD_ERROR.REQUIRED;
  }
  if (isBoolean(agreedToTerms) && !agreedToTerms) {
    errors.agreedToTerms = PAYMENT_METHOD_ERROR.REQUIRED;
  }
  return errors;
};

const createPayHereCheckoutSession = async ({
  post,
  pricingTier,
  paymentLinkId,
  forceLKR,
  setPayhereParams,
  promoCode,
}) => {
  const requestData = {
    pricingTierId: pricingTier.id,
    paymentLinkId,
    paymentMethodId: "local_cc",
    forceLKR,
  };
  if (!isEmpty(promoCode)) {
    requestData.promoCode = promoCode;
  }
  const response = await PaymentServiceAPI.createCheckoutSession({
    postId: post.id,
    data: requestData,
  });
  if (!response.success) {
    store.dispatch(
      setAppSnackNotification({
        severity: "error",
        message: "Unable to process payment",
      })
    );
    return;
  }
  setPayhereParams(response.body.payhereParams);
  setTimeout(() => {
    const element = document.getElementById("payhere-submission-btn");
    if (element !== null) element.click();
  }, 2000);
};

const createStripeCheckoutSession = async ({
  post,
  pricingTier,
  paymentLinkId,
  forceLKR,
  promoCode,
}) => {
  const requestData = {
    pricingTierId: pricingTier.id,
    paymentLinkId,
    paymentMethodId: "foreign_cc",
    forceLKR,
  };
  if (!isEmpty(promoCode)) {
    requestData.promoCode = promoCode;
  }
  const response = await PaymentServiceAPI.createCheckoutSession({
    postId: post.id,
    data: requestData,
  });
  if (!response.success) {
    store.dispatch(
      setAppSnackNotification({
        severity: "error",
        message: "Unable to process payment",
      })
    );
    return;
  }
  const stripe = window.Stripe(Config.STRIPE_KEY);
  stripe.redirectToCheckout({
    sessionId: response.body.sessionId,
  });
};

const onClickPaymentContinueBtn = async ({
  currency,
  paymentLinkData,
  selectedPaymentMethod,
  secondaryPaymentMethod,
  submitCount,
  agreedToTerms,
  setFormErrors,
  setSubmitCount,
  setPayhereParams,
  setFormSubmitting,
  promoCode,
}) => {
  try {
    setFormSubmitting(true);
    setSubmitCount(submitCount + 1);
    const formErrors = validatePaymentForm({
      selectedPaymentMethod,
      secondaryPaymentMethod,
      agreedToTerms,
    });
    if (!isEmpty(formErrors)) {
      setFormErrors(formErrors);
      return;
    }
    const { post, pricingTier, linkId: paymentLinkId } = paymentLinkData;
    if (secondaryPaymentMethod.id === "local_cc") {
      createPayHereCheckoutSession({
        post,
        paymentLinkId,
        pricingTier,
        setPayhereParams,
        forceLKR: currency.code === "LKR",
        promoCode,
      });
    } else if (secondaryPaymentMethod.id === "foreign_cc") {
      createStripeCheckoutSession({
        post,
        paymentLinkId,
        pricingTier,
        forceLKR: false,
        promoCode,
      });
    }
  } catch (error) {
    setTimeout(() => {
      setFormSubmitting(false);
    }, 2000);
    AppLogger.exception(error);
    store.dispatch(
      setAppSnackNotification({
        severity: "error",
        message: "Unable to process payment",
      })
    );
  }
};

const PageDomain = {
  initGenericPaymentMethods,
  initPaymentLinkData,
  onClickPaymentContinueBtn,
  validatePaymentForm,
  initPaymentCurrencyByPaymentLinkData,
  simulateCheckoutTier,
  simulateCheckoutTierWithPromoCode,
  calculateInvoiceObject,
  loadStripeScript,
  loadPaypalScript,
};
export default PageDomain;
